import { useMemo, useCallback } from 'react';
import { useApi } from '../../provider';
import { useQuery, useMutation } from '../../hooks';
import { queryCache } from '../../cache';
import { ApiResponse } from '../../types';
import { MergeService } from './service';
import {
  MergeSuggestionsResponse,
  MergePreview,
  MergeRequest,
  MergeResponse,
  MergeDomainConfig,
} from './types';

/**
 * Generic hook for fetching merge suggestions
 */
export function useMergeSuggestions<T = unknown>(
  config: MergeDomainConfig,
  sourceId: number | null,
  maxResults = 10
) {
  const api = useApi();
  const service = useMemo(() => new MergeService(api, config), [api, config]);
  const fetcher = useCallback(() => {
    if (sourceId === null) {
      throw new Error('Source ID is required');
    }
    return service.getSuggestions(sourceId, maxResults) as Promise<
      ApiResponse<MergeSuggestionsResponse<T>>
    >;
  }, [service, sourceId, maxResults]);

  const cacheKey = sourceId
    ? `${config.domainName}/${sourceId}/merge-suggestions`
    : '';

  return useQuery<MergeSuggestionsResponse<T>>(cacheKey, fetcher, {
    enabled: sourceId !== null,
  });
}

/**
 * Generic hook for fetching merge preview
 */
export function useMergePreview(
  config: MergeDomainConfig,
  sourceId: number | null,
  targetId: number | null
) {
  const api = useApi();
  const service = useMemo(() => new MergeService(api, config), [api, config]);
  const fetcher = useCallback(() => {
    if (sourceId === null || targetId === null) {
      throw new Error('Source ID and Target ID are required');
    }
    return service.getPreview(sourceId, targetId);
  }, [service, sourceId, targetId]);

  const cacheKey =
    sourceId && targetId
      ? `${config.domainName}/${sourceId}/merge-preview/${targetId}`
      : '';

  return useQuery<MergePreview>(cacheKey, fetcher, {
    enabled: sourceId !== null && targetId !== null,
  });
}

/**
 * Generic hook for executing merge operation
 */
export function useMerge(config: MergeDomainConfig, cacheKey: string) {
  const api = useApi();
  const service = useMemo(() => new MergeService(api, config), [api, config]);

  return useMutation<
    MergeResponse,
    { sourceId: number; targetId: number; request: MergeRequest }
  >(
    ({ sourceId, targetId, request }) =>
      service.executeMerge(sourceId, targetId, request),
    {
      onSuccess: () => {
        queryCache.invalidate(cacheKey);
      },
    }
  );
}
